import React, { useState, useEffect, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Compass, Home, ChevronLeft, ChevronRight, Search as SearchIcon, FileText, Globe, BarChart3, Send, Loader2, Lock, ArrowLeft, Copy, CheckCheck, ExternalLink, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AuthContext, API } from "@/App";
import { toast } from "sonner";

const SEOPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [templates, setTemplates] = useState({});
  const [offpage, setOffpage] = useState({});
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generatedPrompt, setGeneratedPrompt] = useState(null);
  const [copied, setCopied] = useState(false);
  const [expandedTask, setExpandedTask] = useState(null);

  // Form state for blog post template
  const [blogForm, setBlogForm] = useState({
    title: "", primary_keyword: "", word_count: 1500, density_percentage: 1.5,
    secondary_keywords: "", h2s: "", tone: "Professional", readability_target: "Grade 8"
  });
  const [metaForm, setMetaForm] = useState({ topic: "", primary_keyword: "", page_type: "blog", count: 3, cta: "" });

  const tier = user?.subscription_tier || "free";
  const hasAccess = user?.is_admin || user?.is_vip || ["premium", "team"].includes(tier);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [tplRes, offRes] = await Promise.all([
        fetch(`${API}/seo/templates`, { credentials: "include" }),
        fetch(`${API}/seo/offpage`, { credentials: "include" })
      ]);
      if (tplRes.ok) setTemplates(await tplRes.json());
      if (offRes.ok) setOffpage(await offRes.json());
    } catch (e) {} finally { setLoading(false); }
  };

  const handleGenerate = async (templateKey, variables) => {
    setGenerating(true); setGeneratedPrompt(null);
    try {
      const res = await fetch(`${API}/seo/generate`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ template: templateKey, variables })
      });
      if (res.ok) { setGeneratedPrompt(await res.json()); toast.success("SEO prompt generated!"); }
      else { const e = await res.json(); toast.error(e.detail || "Generation failed"); }
    } catch (e) { toast.error("Something went wrong"); } finally { setGenerating(false); }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true); toast.success("Copied!"); setTimeout(() => setCopied(false), 2000);
  };

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <Lock className="w-12 h-12 text-purple-400 mx-auto mb-4" />
          <h2 className="font-inter font-bold text-xl mb-2">SEO Tools</h2>
          <p className="text-white/50 text-sm mb-6">On-page and off-page SEO tools are available for Premium subscribers and above.</p>
          <Link to="/pricing"><Button className="bg-purple-700 text-white hover:bg-purple-600">Upgrade to Premium - $25/mo</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark">
      <header className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-white/60 hover:text-white"><ChevronLeft className="w-5 h-5" /></Button>
              <Link to="/" className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-purple-700 flex items-center justify-center"><Compass className="w-4 h-4 text-white" /></div>
                <span className="font-inter font-bold text-lg">promptviser</span>
              </Link>
              <Badge className="bg-neon/10 text-neon border-neon/30 text-[10px]">SEO Tools</Badge>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/"><Button variant="ghost" size="icon" className="text-white/60 hover:text-white"><Home className="w-5 h-5" /></Button></Link>
              <Link to="/dashboard"><Button variant="outline" size="sm" className="border-white/20 text-xs">Dashboard</Button></Link>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-white/40 hover:text-white mb-6 text-sm"><ArrowLeft className="w-4 h-4" />Dashboard</Link>

        <Tabs defaultValue="onpage">
          <TabsList className="bg-dark-paper border border-white/10 p-1">
            <TabsTrigger value="onpage" className="data-[state=active]:bg-purple-700 data-[state=active]:text-white text-xs"><FileText className="w-3.5 h-3.5 mr-1" />On-Page SEO</TabsTrigger>
            <TabsTrigger value="offpage" className="data-[state=active]:bg-purple-700 data-[state=active]:text-white text-xs"><Globe className="w-3.5 h-3.5 mr-1" />Off-Page SEO</TabsTrigger>
          </TabsList>

          {/* On-Page SEO */}
          <TabsContent value="onpage" className="mt-6">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Blog Post Template */}
              <div className="bg-dark-paper border border-white/10 rounded-xl p-5">
                <h3 className="font-inter font-semibold mb-4 flex items-center gap-2"><FileText className="w-4 h-4 text-purple-400" />SEO Blog Post</h3>
                <div className="space-y-3">
                  <div><Label className="text-xs">Title</Label><Input value={blogForm.title} onChange={e => setBlogForm({...blogForm, title: e.target.value})} className="bg-dark-subtle border-white/10 text-sm" placeholder="10 Best AI Tools for Coding" data-testid="seo-title" /></div>
                  <div><Label className="text-xs">Primary Keyword</Label><Input value={blogForm.primary_keyword} onChange={e => setBlogForm({...blogForm, primary_keyword: e.target.value})} className="bg-dark-subtle border-white/10 text-sm" placeholder="AI coding tools" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Word Count</Label><Input type="number" value={blogForm.word_count} onChange={e => setBlogForm({...blogForm, word_count: parseInt(e.target.value) || 1500})} className="bg-dark-subtle border-white/10 text-sm" /></div>
                    <div><Label className="text-xs">Keyword Density %</Label><Input type="number" step="0.1" value={blogForm.density_percentage} onChange={e => setBlogForm({...blogForm, density_percentage: parseFloat(e.target.value) || 1.5})} className="bg-dark-subtle border-white/10 text-sm" /></div>
                  </div>
                  <div><Label className="text-xs">Secondary Keywords (comma-separated)</Label><Input value={blogForm.secondary_keywords} onChange={e => setBlogForm({...blogForm, secondary_keywords: e.target.value})} className="bg-dark-subtle border-white/10 text-sm" placeholder="best coding AI, AI programming" /></div>
                  <div><Label className="text-xs">H2 Headings (comma-separated)</Label><Input value={blogForm.h2s} onChange={e => setBlogForm({...blogForm, h2s: e.target.value})} className="bg-dark-subtle border-white/10 text-sm" placeholder="Features, Pricing, Comparison" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Tone</Label>
                      <Select value={blogForm.tone} onValueChange={v => setBlogForm({...blogForm, tone: v})}>
                        <SelectTrigger className="bg-dark-subtle border-white/10 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["Professional","Casual","Academic","Conversational","Authoritative"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label className="text-xs">Readability</Label><Input value={blogForm.readability_target} onChange={e => setBlogForm({...blogForm, readability_target: e.target.value})} className="bg-dark-subtle border-white/10 text-sm" /></div>
                  </div>
                  <Button onClick={() => handleGenerate("blog_post", blogForm)} disabled={generating || !blogForm.primary_keyword} className="w-full bg-purple-700 text-white hover:bg-purple-600 text-xs font-semibold" data-testid="generate-seo-blog">
                    {generating ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />Generating...</> : <><Send className="w-3.5 h-3.5 mr-1" />Generate SEO Prompt</>}
                  </Button>
                </div>
              </div>

              {/* Meta Description Template */}
              <div className="bg-dark-paper border border-white/10 rounded-xl p-5">
                <h3 className="font-inter font-semibold mb-4 flex items-center gap-2"><SearchIcon className="w-4 h-4 text-purple-400" />Meta Description Generator</h3>
                <div className="space-y-3">
                  <div><Label className="text-xs">Topic</Label><Input value={metaForm.topic} onChange={e => setMetaForm({...metaForm, topic: e.target.value})} className="bg-dark-subtle border-white/10 text-sm" placeholder="AI-powered code review tools" /></div>
                  <div><Label className="text-xs">Primary Keyword</Label><Input value={metaForm.primary_keyword} onChange={e => setMetaForm({...metaForm, primary_keyword: e.target.value})} className="bg-dark-subtle border-white/10 text-sm" placeholder="code review AI" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Page Type</Label>
                      <Select value={metaForm.page_type} onValueChange={v => setMetaForm({...metaForm, page_type: v})}>
                        <SelectTrigger className="bg-dark-subtle border-white/10 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["blog","product","landing","category","service"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label className="text-xs">Number of Variations</Label><Input type="number" value={metaForm.count} onChange={e => setMetaForm({...metaForm, count: parseInt(e.target.value) || 3})} className="bg-dark-subtle border-white/10 text-sm" min={1} max={5} /></div>
                  </div>
                  <div><Label className="text-xs">Call to Action</Label><Input value={metaForm.cta} onChange={e => setMetaForm({...metaForm, cta: e.target.value})} className="bg-dark-subtle border-white/10 text-sm" placeholder="Try free today" /></div>
                  <Button onClick={() => handleGenerate("meta_description", metaForm)} disabled={generating || !metaForm.topic} className="w-full bg-purple-700 text-white hover:bg-purple-600 text-xs font-semibold">
                    {generating ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />Generating...</> : <><Send className="w-3.5 h-3.5 mr-1" />Generate Meta Descriptions</>}
                  </Button>
                </div>
              </div>
            </div>

            {/* Generated Prompt */}
            {generatedPrompt && (
              <div className="mt-6 bg-dark-paper border border-neon/20 rounded-xl p-5" data-testid="generated-seo-prompt">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-inter font-semibold text-sm flex items-center gap-2"><CheckCheck className="w-4 h-4 text-neon" />Generated SEO Prompt</h3>
                  <Button variant="outline" size="sm" onClick={() => handleCopy(generatedPrompt.prompt)} className="border-white/20 text-xs">
                    {copied ? <><CheckCheck className="w-3.5 h-3.5 mr-1 text-neon" />Copied</> : <><Copy className="w-3.5 h-3.5 mr-1" />Copy</>}
                  </Button>
                </div>
                <div className="bg-dark rounded-lg p-4 border border-white/5 mb-4">
                  <p className="font-mono text-xs text-white/70 leading-relaxed whitespace-pre-wrap">{generatedPrompt.prompt}</p>
                </div>
                {generatedPrompt.tips?.length > 0 && (
                  <div className="mb-3">
                    <h4 className="text-xs font-semibold text-white/50 mb-1.5">SEO Tips</h4>
                    <ul className="space-y-1">{generatedPrompt.tips.map((tip, i) => <li key={i} className="text-xs text-white/40 flex items-start gap-1.5"><span className="text-neon mt-0.5">-</span>{tip}</li>)}</ul>
                  </div>
                )}
                {generatedPrompt.keyword_suggestions?.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-white/50 mb-1.5">Keyword Suggestions</h4>
                    <div className="flex flex-wrap gap-1">{generatedPrompt.keyword_suggestions.map((kw, i) => <Badge key={i} variant="secondary" className="text-[10px]">{kw}</Badge>)}</div>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* Off-Page SEO */}
          <TabsContent value="offpage" className="mt-6">
            <h2 className="font-inter font-semibold text-lg mb-4">Off-Page SEO Guidance</h2>
            <p className="text-white/40 text-sm mb-6">Platform matching, step-by-step walkthroughs, and templates for each off-page SEO task.</p>
            <div className="space-y-3">
              {Object.entries(offpage).map(([key, task]) => (
                <div key={key} className="bg-dark-paper border border-white/10 rounded-xl overflow-hidden">
                  <button onClick={() => setExpandedTask(expandedTask === key ? null : key)} className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors" data-testid={`offpage-${key}`}>
                    <div className="flex items-center gap-3">
                      <Globe className="w-4 h-4 text-purple-400" />
                      <span className="font-semibold text-sm">{task.name}</span>
                      {task.platforms && <Badge variant="outline" className="text-[10px] border-white/20">{task.platforms.length} platforms</Badge>}
                    </div>
                    {expandedTask === key ? <ChevronUp className="w-4 h-4 text-white/30" /> : <ChevronDown className="w-4 h-4 text-white/30" />}
                  </button>
                  {expandedTask === key && (
                    <div className="px-4 pb-4 border-t border-white/5 pt-4">
                      {/* Platform Table */}
                      {task.platforms && (
                        <div className="mb-4 overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead><tr className="border-b border-white/10">
                              <th className="text-left p-2 text-white/40">Platform</th>
                              <th className="text-left p-2 text-white/40">Best For</th>
                              <th className="text-left p-2 text-white/40">Price</th>
                              <th className="text-left p-2 text-white/40">Match</th>
                            </tr></thead>
                            <tbody>{task.platforms.map((p, i) => (
                              <tr key={i} className="border-b border-white/5">
                                <td className="p-2 font-semibold text-white/80">{p.name}</td>
                                <td className="p-2 text-white/50">{p.best_for}</td>
                                <td className="p-2 text-white/50">{p.price}</td>
                                <td className="p-2"><Badge className="bg-neon/10 text-neon text-[10px]">{p.match}</Badge></td>
                              </tr>
                            ))}</tbody>
                          </table>
                        </div>
                      )}
                      {/* Steps */}
                      {task.steps && (
                        <div className="mb-4">
                          <h4 className="text-xs font-semibold text-white/50 mb-2">Step-by-Step Guide</h4>
                          <ol className="space-y-1.5">{task.steps.map((step, i) => (
                            <li key={i} className="text-xs text-white/60 flex items-start gap-2">
                              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-900/50 flex items-center justify-center text-[10px] text-purple-300 font-bold mt-0.5">{i + 1}</span>
                              {step.replace(/^\d+\.\s*/, "")}
                            </li>
                          ))}</ol>
                        </div>
                      )}
                      {/* Template */}
                      {task.template && (
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-xs font-semibold text-white/50">Template</h4>
                            <Button variant="outline" size="sm" onClick={() => handleCopy(task.template)} className="border-white/20 text-[10px] h-6"><Copy className="w-3 h-3 mr-1" />Copy</Button>
                          </div>
                          <div className="bg-dark rounded-lg p-3 border border-white/5">
                            <p className="font-mono text-[11px] text-white/60 whitespace-pre-wrap leading-relaxed">{task.template}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default SEOPage;
